/**
 * Minimum Quantity Handler
 * Validates quantity inputs based on product metafields
 */
class MinimumQuantityHandler {
  constructor() {
    this.init();
  }

  init() {

    this.initProductQuantityValidation();
    
    this.initCartQuantityValidation();
    
    this.hookIntoCartUpdates();
    
    this.hookIntoProductFormSubmissions();
  }

  initProductQuantityValidation() {
    const productQuantityInputs = document.querySelectorAll('.quantity-field[data-minimum-quantity-enabled="true"]');
    
    productQuantityInputs.forEach(input => {
      this.addQuantityValidation(input);
    });
  }

  initCartQuantityValidation() {
    const cartQuantityInputs = document.querySelectorAll('[data-quantity-key][data-minimum-quantity-enabled="true"]');
    
    cartQuantityInputs.forEach(input => {
      this.addQuantityValidation(input);
    });
  }

  hookIntoCartUpdates() {
    const cartInputs = document.querySelectorAll('[data-quantity-key][data-minimum-quantity-enabled="true"]');
    
    cartInputs.forEach(input => {
      input.addEventListener('input', (event) => {
        this.updateCartMeterDisplay(event.target);
      });
      
      const validateCartQuantity = (event) => {
        if (event.target.dataset.validating === 'true') {
          return;
        }
        
        event.target.dataset.validating = 'true';
        
        const minimumQuantity = parseInt(event.target.dataset.minimumQuantity) || 1;
        const maximumQuantity = parseInt(event.target.dataset.maxQuantity) || 999999;
        const originalValue = parseInt(event.target.value) || 0;
        let currentValue = originalValue;
        
        if (currentValue > 0 && currentValue < minimumQuantity) {
          currentValue = minimumQuantity;
          event.target.value = minimumQuantity;
          this.showMinimumQuantityMessage(event.target, minimumQuantity);
          // Update meter display with the adjusted quantity
          this.updateCartMeterDisplay(event.target);
        }
        
        if (maximumQuantity < 999999 && currentValue > maximumQuantity) {
          currentValue = maximumQuantity;
          event.target.value = maximumQuantity;
          this.showInventoryMessage(event.target, maximumQuantity, originalValue);
          // Update meter display with the adjusted quantity
          this.updateCartMeterDisplay(event.target);
        }
        
        // Always trigger theme update if value changed (even if adjusted)
        if (currentValue !== originalValue) {
          console.log('Triggering theme cart update with final quantity:', currentValue);
          this.triggerThemeCartUpdate(event.target, currentValue);
        }
        
        setTimeout(() => {
          event.target.dataset.validating = 'false';
        }, 100);
      };
      
      input.addEventListener('change', validateCartQuantity);
      input.addEventListener('blur', validateCartQuantity);
    });
  }

  hookIntoProductFormSubmissions() {
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    productForms.forEach(form => {
      form.addEventListener('submit', (event) => {
        const quantityInput = form.querySelector('.quantity-field[data-minimum-quantity-enabled="true"]');
        
        if (quantityInput) {
          const isValid = this.validateBeforeCartUpdate(quantityInput);
          
          if (!isValid) {
            event.preventDefault();
            return false;
          }
        }
      });
    });
    
    const addToCartButtons = document.querySelectorAll('[data-product-atc]');
    
    addToCartButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        const form = button.closest('form');
        if (form) {
          const quantityInput = form.querySelector('.quantity-field[data-minimum-quantity-enabled="true"]');
          
          if (quantityInput) {
            const isValid = this.validateBeforeCartUpdate(quantityInput);
            
            if (!isValid) {
              event.preventDefault();
              event.stopPropagation();
              return false;
            }
          }
        }
      });
    });
  }

  addQuantityValidation(input) {
    const minimumQuantity = parseInt(input.dataset.minimumQuantity) || 1;
    
    input.addEventListener('change', (event) => {
      this.validateQuantity(event.target, minimumQuantity);
      this.updateMeterProperty(event.target);
    });
    
    input.addEventListener('blur', (event) => {
      this.validateQuantity(event.target, minimumQuantity);
      this.updateMeterProperty(event.target);
    });

    input.addEventListener('keydown', (event) => {
      this.handleKeydown(event, minimumQuantity);
    });

    input.addEventListener('paste', (event) => {
      setTimeout(() => {
        this.validateQuantity(event.target, minimumQuantity);
        this.updateMeterProperty(event.target);
      }, 10);
    });
    
    input.addEventListener('input', (event) => {
      this.updateMeterProperty(event.target);
    });
  }

  validateQuantity(input, minimumQuantity) {
    const currentValue = parseInt(input.value) || 0;
    
    if (input.value !== '' && currentValue > 0 && currentValue < minimumQuantity) {
      input.value = minimumQuantity;
      this.showMinimumQuantityMessage(input, minimumQuantity);
      this.updateMeterProperty(input);
    }

    this.removeErrorStyling(input);
    
    if (input.value !== '' && currentValue > 0 && currentValue < minimumQuantity) {
      this.addErrorStyling(input);
    }
  }

  validateBeforeCartUpdate(input) {
    if (!input.dataset.minimumQuantityEnabled) return true;
    
    const minimumQuantity = parseInt(input.dataset.minimumQuantity) || 1;
    const currentValue = parseInt(input.value) || 0;
    
    if (currentValue > 0 && currentValue < minimumQuantity) {
      input.value = minimumQuantity;
      this.showMinimumQuantityMessage(input, minimumQuantity);
      this.updateMeterProperty(input);
      return false;
    }
    
    return true;
  }

  handleKeydown(event, minimumQuantity) {
    if ([46, 8, 9, 27, 13, 110, 190].indexOf(event.keyCode) !== -1 ||
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true) ||
        (event.keyCode >= 35 && event.keyCode <= 40)) {
      return;
    }
    
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }

  showMinimumQuantityMessage(input, minimumQuantity) {
    this.removeMinimumQuantityMessage(input);
    
    const message = document.createElement('div');
    message.className = 'minimum-quantity-message';
    message.textContent = `Minimale hoeveelheid is ${minimumQuantity}`;
    message.style.cssText = 'color: #d4443c; font-size: 0.875rem; margin-top: 0.25rem; font-weight: 500; word-wrap: break-word; white-space: normal; line-height: 1.2; max-width:125px; width: 125px; display: block;';
    
    const insertTarget = input.parentNode;
    const nextSibling = input.nextSibling;
    
    if (nextSibling) {
      insertTarget.insertBefore(message, nextSibling);
    } else {
      insertTarget.appendChild(message);
    }
    
    setTimeout(() => {
      this.removeMinimumQuantityMessage(input);
    }, 4000);
  }

  removeMinimumQuantityMessage(input) {
    const existingMessage = input.parentNode.querySelector('.minimum-quantity-message');
    if (existingMessage) {
      existingMessage.remove();
    }
  }

  addErrorStyling(input) {
    input.classList.add('minimum-quantity-error');
    input.style.borderColor = '#d4443c';
    input.style.boxShadow = '0 0 0 1px #d4443c';
  }

  removeErrorStyling(input) {
    input.classList.remove('minimum-quantity-error');
    input.style.borderColor = '';
    input.style.boxShadow = '';
  }

  // Method to reinitialize after AJAX cart updates
  reinitialize() {
    // Remove existing event listeners by clearing and reinitializing
    this.init();
  }
  
  updateMeterProperty(input) {
    if (!input.dataset.minimumQuantityEnabled) return;
    
    const quantity = parseInt(input.value) || 0;
    const meters = (quantity * 0.1).toFixed(1);
    
    // Update the meter display
    const form = input.closest('form');
    if (form) {
      const meterDisplay = form.querySelector('[data-meters-value]');
      if (meterDisplay) {
        meterDisplay.textContent = meters;
      }
      
      // Update the hidden property field
      const meterProperty = form.querySelector('[data-meter-property]');
      if (meterProperty) {
        // Get the meter unit text from the display element
        const meterUnit = form.querySelector('[data-quantity-meters]')?.textContent?.match(/\s(\w+)$/)?.[1] || 'meter';
        meterProperty.value = `${meters} ${meterUnit}`;
      }
    }
  }
  
  updateCartMeterDisplay(input) {
    if (!input.dataset.minimumQuantityEnabled) return;
    
    const quantity = parseInt(input.value) || 0;
    if (quantity === 0) return; // Don't update for deletion
    
    const meters = (quantity * 0.1).toFixed(1);
    const cartItem = input.closest('tr.cart-item');
    
    if (cartItem) {
      // Find all property values and look for the meter one specifically
      const allPropertyValues = cartItem.querySelectorAll('.cart-item-property-value');
      let meterDisplay = null;
      
      allPropertyValues.forEach(prop => {
        const parentProperty = prop.closest('.cart-item-property');
        if (parentProperty) {
          const label = parentProperty.querySelector('.cart-item-property-label');
          if (label && (label.textContent.includes('Meter:') || label.textContent.includes('meter:'))) {
            meterDisplay = prop;
          }
        }
      });
      
      if (meterDisplay) {
        meterDisplay.textContent = `${meters} meter`;
      }
    }
  }
  
  triggerThemeCartUpdate(input, quantity) {
    if (!input.dataset.quantityKey || !input.dataset.minimumQuantityEnabled) return;

    console.log('Triggering theme cart update with quantity:', quantity);

    // Update the input value to the corrected quantity
    input.value = quantity;

    const event = new Event('input', {
      bubbles: true,
      cancelable: true
    });


    input.dispatchEvent(event);
  }
  

  

  showInventoryMessage(input, availableQuantity, requestedQuantity) {
    // Prevent duplicate messages
    if (input.dataset.showingInventoryMessage === 'true') {
      return;
    }
    
    this.removeInventoryMessage(input);
    input.dataset.showingInventoryMessage = 'true';
    
    console.log('Showing inventory message:', {
      available: availableQuantity,
      requested: requestedQuantity
    });
    
    const message = document.createElement('div');
    message.className = 'inventory-limitation-message';
    message.textContent = `Slechts ${availableQuantity} beschikbaar. Hoeveelheid aangepast van ${requestedQuantity} naar ${availableQuantity}.`;
    message.style.cssText = 'color: #d4443c; font-size: 0.875rem; margin-top: 0.25rem; font-weight: 500; word-wrap: break-word; white-space: normal; line-height: 1.2; max-width:125px; display: block;';
    
    const insertTarget = input.parentNode;
    const nextSibling = input.nextSibling;
    
    if (nextSibling) {
      insertTarget.insertBefore(message, nextSibling);
    } else {
      insertTarget.appendChild(message);
    }
    
    setTimeout(() => {
      this.removeInventoryMessage(input);
      input.dataset.showingInventoryMessage = 'false';
    }, 5000);
  }

  removeInventoryMessage(input) {
    const existingMessage = input.parentNode.querySelector('.inventory-limitation-message');
    if (existingMessage) {
      existingMessage.remove();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const minimumQuantityHandler = new MinimumQuantityHandler();
  
  // Make instance globally available for cart updates
  window.minimumQuantityHandler = minimumQuantityHandler;
});

// Reinitialize after cart updates (for AJAX cart themes)
document.addEventListener('cart:updated', () => {
  if (window.minimumQuantityHandler) {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      window.minimumQuantityHandler.reinitialize();
    }, 100);
  }
});

// Also listen for any cart changes
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        // Check if cart table was updated
        const cartTable = mutation.target.querySelector('[data-cart-table]');
        if (cartTable && window.minimumQuantityHandler) {
          setTimeout(() => {
            window.minimumQuantityHandler.reinitialize();
          }, 50);
        }
      }
    });
  });
  
  // Start observing cart updates
  document.addEventListener('DOMContentLoaded', () => {
    const cartContainer = document.querySelector('[data-cart-table]');
    if (cartContainer) {
      observer.observe(cartContainer.parentNode, { 
        childList: true, 
        subtree: true 
      });
    }
  });
} 